import { useEffect } from "react";

// Controls a Tab can land on. A disabled or hidden-type control never takes focus, so it must not count as the
// first or last stop; `iframe` is there for the Stripe card fields, which are the last stop of a payment dialog.
const TRAP_FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex='-1'])";

// Open traps, oldest first. Only the last one acts, so a dialog opened over another does not fight it for focus.
const openFocusTraps = [];

// getClientRects() is always empty in jsdom, so rendered-ness is read from the computed style of the control and
// each ancestor up to the dialog: a collapsed section or a `display: none` wrapper hides every control inside it.
// An `inert` subtree (rows a confirmation covers) takes no focus either, so it is never an end of the cycle.
const isUsableControl = (control, container) => {
  if (control.closest("[inert]") || getComputedStyle(control).visibility === "hidden") {
    return false;
  }

  for (let node = control; node && node !== container.parentElement; node = node.parentElement) {
    if (getComputedStyle(node).display === "none") {
      return false;
    }
  }

  return true;
};

/**
 * Keeps keyboard focus inside an open dialog.
 *
 * Tab and Shift+Tab are handled for the whole document while the dialog is mounted, so they also work when focus is
 * on the dialog container itself or on <body> (after a click on plain text). Focus outside the dialog goes to the
 * first control on Tab and to the last on Shift+Tab; at either end it wraps. A `focusin` guard sends focus that lands
 * outside anyway (programmatic focus, a click on the page behind) back to the first control.
 *
 * Initial focus, focus restore, Escape and scroll lock stay with the dialog, as they differ per dialog. Declare this
 * hook before the effect that restores focus, so the trap is gone by the time the opener is refocused.
 *
 * @param {{ current: HTMLElement | null }} containerRef The dialog element.
 * @param {boolean} [enabled=true] False while the container is mounted but is not acting as a dialog yet (a search
 *   that is a bar on a wide screen and a full-screen dialog on a phone); the trap runs only while this is true.
 */
export const useFocusTrap = (containerRef, enabled = true) => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const token = {};
    openFocusTraps.push(token);

    const isTopmost = () => openFocusTraps[openFocusTraps.length - 1] === token;

    const getControls = container =>
      Array.from(container.querySelectorAll(TRAP_FOCUSABLE_SELECTOR)).filter(control => isUsableControl(control, container));

    const handleKeyDown = event => {
      const container = containerRef.current;
      if (event.key !== "Tab" || !container || !isTopmost()) {
        return;
      }

      const controls = getControls(container);
      if (!controls.length) {
        event.preventDefault();
        container.focus?.();
        return;
      }

      const first = controls[0];
      const last = controls[controls.length - 1];
      const active = document.activeElement;
      const isInside = active !== container && container.contains(active);

      if (!isInside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      // `active` may be a control left out of `controls` (tabindex="-1", or one that just became hidden), so the ends
      // are read by document order, not by identity.
      const order = Array.from(container.querySelectorAll("*"));
      const activeIndex = order.indexOf(active);
      const isAtStart = activeIndex <= order.indexOf(first);
      const isAtEnd = activeIndex >= order.indexOf(last);

      if (event.shiftKey && isAtStart) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && isAtEnd) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleFocusIn = event => {
      const container = containerRef.current;
      if (!container || !container.isConnected || !isTopmost() || container.contains(event.target)) {
        return;
      }

      const [first] = getControls(container);
      (first || container).focus?.();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("focusin", handleFocusIn);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("focusin", handleFocusIn);
      openFocusTraps.splice(openFocusTraps.indexOf(token), 1);
    };
  }, [containerRef, enabled]);
};
